import React from "react";

const SecurityNotice: React.FC = () => {
  const tips = [
    "Do not share OTP or passwords with anyone",
    "Verify official email domains before clicking links",
    "We never call to collect advance payments",
    "Check for HTTPS before submitting any data"
  ];

  return (
    <section className="w-full px-6 py-12 md:py-16 bg-[#FFFDFB] relative overflow-hidden font-sans select-none">
      <div className="max-w-5xl mx-auto w-full">
        <div className="relative overflow-hidden rounded-xl md:rounded-[28px] p-6 sm:p-8 md:p-10 bg-[#0B1220] border border-white/[0.05] shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
          
          {/* Background sheen and blur orbs */}
          <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.01] via-transparent to-white/[0.04] pointer-events-none" />
          <div className="absolute top-[-30%] right-[-10%] w-[250px] h-[250px] bg-[#DE3226] opacity-[0.05] rounded-full blur-[80px] pointer-events-none" />
 
          {/* Grid Container */}
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            
            {/* Left Column: Heading, alert details and 2x2 tips (7 cols) */}
            <div className="lg:col-span-7 flex flex-col gap-6 w-full text-left">
              
              {/* Header Title with warning icon */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5.5 h-5.5 text-[#DE3226] animate-pulse" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="w-fit px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] text-[9px] font-black tracking-widest text-[#DE3226] uppercase leading-none">
                    Fraud Alert
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight leading-none">
                    Security <span className="text-[#DE3226]">Notice</span>
                  </h2>
                </div>
              </div>

              {/* Attention Statement */}
              <div className="border-l-[3px] border-l-[#DE3226] pl-4">
                <p className="text-[#94A3B8] font-bold text-[13px] sm:text-[14px] leading-relaxed max-w-xl">
                  <span className="text-[#DE3226] font-black">Attention:</span> Fraudsters are impersonating our employees via calls & social media to collect fraudulent advance payments. Stay vigilant and protect yourself.
                </p>
              </div>

              {/* 2x2 Tips Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-2">
                {tips.map((tip, i) => (
                  <div 
                    key={i}
                    className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-3.5 flex items-center gap-3 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="flex-shrink-0 w-4.5 h-4.5 rounded-full bg-white/[0.04] border border-white/[0.1] flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-[#DE3226]" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-300 font-extrabold text-[12px] sm:text-[12.5px] leading-snug">
                      {tip}
                    </span>
                  </div>
                ))}
              </div>

            </div>

            {/* Right Column: Safe Payment card and Certification Badges (5 cols) */}
            <div className="lg:col-span-5 flex flex-col gap-4 w-full justify-between">
              
              {/* Safe Payment Protocol Card */}
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-[20px] p-5 sm:p-6 flex flex-col gap-4">
                
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-slate-400">
                    <svg className="w-3.5 h-3.5 text-[#DE3226]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-black text-slate-400 uppercase tracking-widest">
                    Safe Payment Protocol
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="text-[13px] sm:text-[14px] font-black text-white leading-snug mt-1">
                    Pay only through our official website or authorised secure payment links.
                  </p>
                  <div className="border-t border-white/[0.06] pt-3">
                    <p className="text-slate-500 font-medium italic text-[11px] sm:text-[12px] leading-relaxed">
                      भुगतान केवल आधिकारिक वेबसाइट से करें किसी अज्ञात लिंक पर भरोसा न करें
                    </p>
                  </div>
                </div>

              </div>

              {/* Certifications Row */}
              <div className="grid grid-cols-2 gap-4">
                
                {/* RBI Badge */}
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-[16px] p-4 flex flex-col gap-2 items-start text-left">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-[#DE3226]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-white font-black text-[12px] sm:text-[13px]">
                      RBI Registered
                    </span>
                    <span className="text-slate-500 font-bold text-[10px] mt-0.5">
                      Licensed NBFC
                    </span>
                  </div>
                </div>

                {/* SSL Badge */}
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-[16px] p-4 flex flex-col gap-2 items-start text-left">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-[#DE3226]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-white font-black text-[12px] sm:text-[13px]">
                      256-Bit SSL
                    </span>
                    <span className="text-slate-500 font-bold text-[10px] mt-0.5">
                      End-to-end encrypted
                    </span>
                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default SecurityNotice;
